import { useCallback, useEffect, useState } from 'react';

import { ApiError } from '../../api/client';
import { campaignsApi, contactsApi, type Campaign, type CampaignField, type Contact } from '../../api/campaigns';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  FormField,
  HelpIcon,
  Input,
  LinkButton,
  Metric,
  CardGrid,
  Section,
  StatusChip,
} from '../../components';
import { useI18n, type TranslationKey } from '../../i18n';
import { useSession } from '../../auth/session-context';
import { CallPanel } from '../../telephony/CallPanel';
import { DialerPanel } from '../../telephony/DialerPanel';
import { dialerApi, type CampaignRuntime } from '../../telephony/telephony-api';

/**
 * Telefoniebereich einer Kampagne.
 *
 * Oben der Dialer - der Normalfall ab Phase 5. Darunter bleibt der einzelne
 * Anruf von Hand erhalten (05_dialer.md nennt manuelles Wählen ausdrücklich),
 * aber nur, solange keine Bereitschaft läuft: zwei Wege gleichzeitig wären
 * für niemanden nachvollziehbar.
 *
 * Administratoren sehen zusätzlich, was in der Kampagne gerade los ist.
 */
export function PhoneTab({ campaign }: { campaign: Campaign }): JSX.Element {
  const { t, tError } = useI18n();
  const { principal, handleApiError } = useSession();

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [fields, setFields] = useState<CampaignField[]>([]);
  const [selected, setSelected] = useState<Contact | null>(null);
  const [manuell, setManuell] = useState(false);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  const isAdmin = principal?.kind === 'tenant_user' && principal.isAdmin;

  const load = useCallback(async () => {
    setError(null);
    try {
      const [liste, felder] = await Promise.all([
        contactsApi.list(campaign.id, { query: query || undefined, limit: 20 }),
        campaignsApi.listFields(campaign.id),
      ]);
      setContacts(liste.contacts);
      setFields(felder.fields);
    } catch (caught) {
      handleApiError(caught);
      setError(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
    }
  }, [campaign.id, handleApiError, query, tError]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="steps">
      {error ? <Alert tone="error">{error}</Alert> : null}

      {isAdmin ? <KampagnenZustand campaign={campaign} /> : null}

      <DialerPanel
        campaign={campaign}
        fields={fields}
        onFinished={() => {
          setSelected(null);
          void load();
        }}
      />

      <Card>
        <Section
          title={t('dialer.manualTitle')}
          description={t('dialer.manualHint')}
          actions={
            <LinkButton onClick={() => setManuell((offen) => !offen)}>
              {manuell ? t('action.close') : t('dialer.manualTitle')}
            </LinkButton>
          }
        >
          {manuell ? (
            <>
              <CallPanel
                campaign={campaign}
                contact={selected}
                fields={fields}
                onFinished={() => {
                  setSelected(null);
                  void load();
                }}
              />

              <FormField label={t('contacts.search')} hint={t('contacts.searchHint')}>
                <Input type="search" value={query} onChange={setQuery} />
              </FormField>

              {selected ? (
                <LinkButton onClick={() => setSelected(null)}>{t('call.manualNumber')}</LinkButton>
              ) : null}

              {contacts.length === 0 ? (
                <EmptyState title={t('contacts.noResults')} />
              ) : (
                <ul className="list">
                  {contacts.map((contact) => (
                    <li key={contact.id} className="list-row">
                      <div className="list-row-text">
                        <strong>{contact.phone}</strong>
                        <span className="field-hint">
                          {[contact.firstName, contact.lastName].filter(Boolean).join(' ')}
                        </span>
                      </div>
                      <Button variant="ghost" onClick={() => setSelected(contact)}>
                        {t('campaigns.open')}
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : null}
        </Section>
      </Card>
    </div>
  );
}

/**
 * Was gerade in der Kampagne passiert - nur für Administratoren.
 *
 * Bewusst knapp: wer ist bereit, wie viele Kunden warten. Die Auswertung
 * kommt mit der Statistik.
 */
function KampagnenZustand({ campaign }: { campaign: Campaign }): JSX.Element | null {
  const { t } = useI18n();
  const { handleApiError } = useSession();
  const [zustand, setZustand] = useState<CampaignRuntime | null>(null);

  useEffect(() => {
    const holen = (): void => {
      void dialerApi
        .campaignState(campaign.id)
        .then(setZustand)
        .catch((caught: unknown) => handleApiError(caught));
    };
    holen();
    const timer = window.setInterval(holen, 3_000);
    return () => window.clearInterval(timer);
  }, [campaign.id, handleApiError]);

  if (!zustand) {
    return null;
  }

  const bereite = zustand.callers.filter((caller) => caller.status === 'AVAILABLE').length;

  return (
    <Card>
      <Section title={t('dialer.runtimeTitle')}>
        {zustand.callers.length === 0 && zustand.waiting === 0 ? (
          <p className="lead">{t('dialer.runtimeIdle')}</p>
        ) : (
          <>
            <CardGrid>
              <Metric label={t('dialer.runtimeCallers')} value={`${bereite} / ${zustand.callers.length}`} />
              <Metric
                label={t('dialer.runtimeWaiting')}
                value={String(zustand.waiting)}
                help={<HelpIcon text={t('dialer.help.waiting')} label={t('dialer.runtimeWaiting')} />}
              />
            </CardGrid>

            <ul className="list">
              {zustand.callers.map((caller) => (
                <li key={caller.userId} className="list-row">
                  <span className="list-row-text">{caller.userId}</span>
                  <StatusChip tone={caller.status === 'IN_CALL' ? 'erfolg' : 'info'}>
                    {t(('dialer.state.' + caller.status) as TranslationKey)}
                  </StatusChip>
                </li>
              ))}
            </ul>
          </>
        )}
      </Section>
    </Card>
  );
}
